import { Box, Button, SortDirection, Table, theme } from '@factorialco/deprecated-design-system'
import {
  InstalledSoftwareListEmployeeSoftwareMembershipFragment,
  SoftwareCardSoftwareSetupFragment,
} from 'generated/resources/graphql'
import { orderBy } from 'lodash'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import createComputed from 'modules/core/hooks/createComputed'
import usePagination from 'modules/core/hooks/usePagination'
import i18n from 'modules/core/lib/i18n'

import SoftwareCard from 'modules/software_management/components/softwareByEmployee/SoftwareCard'
import SoftwareList from 'modules/software_management/components/softwareByEmployee/SoftwareList'
import { getSoftware } from 'modules/software_management/lib/software'

interface SoftwareListProps {
  softwareSetups: SoftwareCardSoftwareSetupFragment[]
  employeeSoftwareMemberships: InstalledSoftwareListEmployeeSoftwareMembershipFragment[]
  removeSoftware: (membershipId: number) => void
  loadingDeleteSoftwareMembership: boolean
  currency: string
}

const useSortEmployeeSoftwareMemberships = createComputed(
  (
    employeeSoftwareMemberships: InstalledSoftwareListEmployeeSoftwareMembershipFragment[],
    direction: SortDirection
  ) => {
    return orderBy(
      employeeSoftwareMemberships,
      (employeeSoftwareMemberships: InstalledSoftwareListEmployeeSoftwareMembershipFragment) =>
        getSoftware(employeeSoftwareMemberships).name,
      [direction]
    )
  }
)

const InstalledSoftwareList = ({
  employeeSoftwareMemberships,
  softwareSetups,
  removeSoftware,
  loadingDeleteSoftwareMembership,
  currency,
}: SoftwareListProps) => {
  const softwareSetupsBySoftware = softwareSetups.reduce<
    Record<string, SoftwareCardSoftwareSetupFragment>
  >((accum, softwareSetup) => {
    if (softwareSetup.software) {
      accum[softwareSetup.software?.id] = softwareSetup
    } else if (softwareSetup.customSoftware?.id) {
      accum[softwareSetup.customSoftware?.id] = softwareSetup
    }
    return accum
  }, {})

  const sortedEmployeeSoftwareMemberships = useSortEmployeeSoftwareMemberships(
    employeeSoftwareMemberships,
    'asc'
  ).get()

  const { paginatedItems, loadMore, hasMore, page } = usePagination({
    batchSize: 20,
    items: sortedEmployeeSoftwareMemberships,
  })

  return (
    <SoftwareList
      title={i18n.t('software_management.employee_software.detail.installed_software.title')}
      subtitle={i18n.t('software_management.employee_software.detail.installed_software.subtitle')}
      icon="check"
      hasMore={hasMore}
      loadMore={loadMore}
      page={page}
      paginatedItems={paginatedItems}
    >
      {employeeSoftwareMemberships.map((membership) => {
        const software = getSoftware(membership)
        const softwareSetup = softwareSetupsBySoftware[software.id]
        if (!softwareSetup) {
          return
        }

        return (
          <Table.Row key={softwareSetup.id}>
            <Table.Cell colSpan={1} noBorder wide padding="s0">
              <Box style={{ marginBottom: theme.dimensions.s24 }}>
                <SoftwareCard
                  key={membership.id}
                  software={software}
                  softwareSetup={softwareSetup}
                  currency={currency}
                >
                  <PopoverMenu type="list">
                    <PopoverMenu.Trigger>
                      <Button.Decorative type="secondary" iconName="ellipsis" />
                    </PopoverMenu.Trigger>
                    <PopoverMenu.Content side="bottom" align="start">
                      <PopoverMenu.TouchableOption
                        icon="delete"
                        danger
                        title={i18n.t(
                          'software_management.employee_software.detail.remove_software'
                        )}
                        onClick={() => removeSoftware(membership.id)}
                        disabled={loadingDeleteSoftwareMembership}
                      />
                    </PopoverMenu.Content>
                  </PopoverMenu>
                </SoftwareCard>
              </Box>
            </Table.Cell>
          </Table.Row>
        )
      })}
    </SoftwareList>
  )
}

export default InstalledSoftwareList
